import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuClock9 } from "react-icons/lu";
import { FaHeart } from "react-icons/fa";

import { getSavedProperties } from "@/api/savedPreference";
import { onUnsavePress } from "@/api/srpFilter";
import imageOnTheWay from "../../../../../public/assets/images/imageOnTheWay.webp";
import Timer from "./Timer";
import BidText from "./BidText";
import BidButton from "./BidButton";
import PriceAndLabel from "@/components/srp/CardComponents/PriceAndLabel";
import { ShimmerContentBlock } from "react-shimmer-effects";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
} from "@/utils/analytics";
import { isPhoneNoVerified } from "@/api/bid";
import { checkLoginStatus } from "@/api/myProfile";
import { getBidCenterDetails, getPDPInitData } from "@/api/PDP";
import {
  enableOwnItNow,
  fetchPDPBidCenterDataSuccess,
  fetchPDPInitialLoadSuccess,
  fetchPDPLatestBidCenterData,
  setSelectedBidAmount,
} from "@/redux/PDP/pdpSlice";
import { setListingId } from "@/bidFeature/propertyDetailSlice";
import { useNavigate } from "react-router";
import { navigateToProperty } from "@/utils/propertyNavigation";
import { markInternalNavigation } from "@/utils/attributionCapture";

const SavedProperties = ({ isSidebarVisible }) => {
  const user = useSelector((state) => state.user);
  const [propertyData, setPropertyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasError, setHasError] = useState(false);
  const initialFetchDone = useRef(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resultsPerPage = 3;

  const fetchPropertyData = useCallback(
    async (page, size) => {
      try {
        setLoading(true);
        const { propertyWatchResponseList, watchCount } =
          await getSavedProperties(
            user.user.userEmailId,
            (page - 1) * size,
            size
          );

        if (propertyWatchResponseList) {
          setPropertyData((prev) => [...prev, ...propertyWatchResponseList]);
          setHasMore(watchCount > page * size);
        } else {
          setPropertyData([]);
        }
      } catch (error) {
        console.error("Error fetching property data:", error);
      } finally {
        setLoading(false);
      }
    },
    [user.user.userEmailId]
  );

  const removeFromFavs = async (propertyData) => {
    const propertyId = propertyData?.propertyDetails?.propertyId;
    const listingId = propertyData?.propertyListingDetails?.listingId;
    try {
      let eventCategory = getEventCategory();

      trackButtonClick({
        eventName: "favourite_property_unsave_click",
        eventCategory: `${eventCategory} - Save List Slider`,
        eventSource: "buyer_dashboard_save_list",
        eventLabel: `Property Id : ${propertyId} | listingId : ${listingId} | propertyStatus : SCHEDULED`,
      });

      const response = await onUnsavePress({ propertyId: propertyId });

      if (response === "success") {
        let eventCategory = getEventCategory();

        trackApiCall({
          eventName: "favourite_property_unsaved",
          eventCategory: `${eventCategory} - Save List Slider`,
          eventSource: "buyer_dashboard_save_list",
          eventLabel: `Property Id : ${propertyId}`,
        });
      } else {
        trackApiCall({
          eventName: "favourite_property_unsave_error",
          eventCategory: `Error - unable to delete`,
          eventSource: "buyer_dashboard_save_list",
          eventLabel: `Property Id : ${propertyId}`,
        });
      }
      setCurrentPage(1);

      setPropertyData([]);
      fetchPropertyData(1, resultsPerPage);
    } catch (error) {
      console.error("Error removing from favorites:", error);
    }
  };

  const imageUrl = (img) => {
    return hasError || !img
      ? imageOnTheWay
      : img.indexOf("https") > -1
      ? img
      : imageOnTheWay;
  };

  const onNavigatePDP = (propertyDetails, source = "general") => {
    const id = propertyDetails?.propertyId;
    let eventCategory = getEventCategory();

    markInternalNavigation()

    trackButtonClick({
      eventName: "save_list_pdp_navigate",
      eventCategory: `${eventCategory}`,
      eventSource: "Property image click",
      eventLabel: `Property Id : ${id} | Source: Save List Drawer`,
      eventParams: { source: source === "button" ? "button" : "image" },
    });

    navigateToProperty(`/property/${id}`, propertyDetails?.commonAttributes?.propCategory, propertyDetails?.commonAttributes?.status);
  };

  // Initial load - should run only once
  useEffect(() => {
    if (!initialFetchDone.current) {
      fetchPropertyData(1, resultsPerPage);
      initialFetchDone.current = true;
    }
  }, [fetchPropertyData]);

  // Load more handler
  const handleLoadMore = () => {
    let eventCategory = getEventCategory();

    trackButtonClick({
      eventName: "show_more_watch_list",
      eventCategory: `${eventCategory} - Save List Slider`,
      eventSource: "Load more button click",
      eventLabel: propertyData
        .map((data) => data?.propertyDetails?.propertyId)
        .join(","),
    });

    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPropertyData(nextPage, resultsPerPage);
    }
  };

  const handleEndAuctionClick = async (propertyData) => {
    const propertyId = propertyData?.propertyDetails?.propertyId;
    const listingId = propertyData?.propertyListingDetails?.listingId;

    try {
      let eventCategory = getEventCategory();

      trackButtonClick({
        eventName: "user_type_check",
        eventCategory: eventCategory,
        eventSource: "End auction click",
      });

      const loginResponse = await checkLoginStatus();

      if (loginResponse) {
        const isPhoneVerifiedResponse = await isPhoneNoVerified(
          loginResponse.primaryPhone
        );

        trackApiCall({
          eventName: "user_type_check_success",
          eventCategory: eventCategory,
          eventSource: "End auction click api call",
          eventParams: { user_type_check_result: true },
        });

        trackApiCall({
          eventName: "own_it_now",
          eventCategory: eventCategory,
          eventSource: "End auction api call",
          eventLabel: `listingId: ${listingId} | Source: Save List Drawer`,
        });

        const data = await getBidCenterDetails(listingId);
        const details = await getPDPInitData(listingId);
        dispatch(fetchPDPBidCenterDataSuccess(data));
        dispatch(fetchPDPLatestBidCenterData(listingId));
        dispatch(fetchPDPInitialLoadSuccess(details));
        dispatch(setListingId(propertyData?.propertyListingDetails));
        dispatch(setSelectedBidAmount(null));
        dispatch(enableOwnItNow(true));

        document.body.style.overflow = "";
        document.body.style.padding = "";
        navigate("/bid");
      }
    } catch (error) {
      console.log(
        "🚀 ~ SavedProperties.jsx:154 ~ handleEndAuctionClick ~ error:",
        error
      );
      trackApiCall({
        eventName: "own_it_now",
        eventCategory: "Error - handleEndAuctionClick",
        eventSource: "End auction api error",
      });
    }
  };

  return (
    <>
      {loading && (
        <div className="mt-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="w-100 mb-3">
              <ShimmerContentBlock
                cta
                card
                thumbnailWidth={130}
                thumbnailHeight={100}
              />
            </div>
          ))}
        </div>
      )}

      {propertyData.length === 0 && !loading && (
        <h3 className="no-data">No Saved Property available</h3>
      )}
      {propertyData.length > 0 &&
        !loading &&
        propertyData.map((data) => (
          <div key={data?.propertyDetails?.id} className="property-saved">
            <div className="image-div">
              <img
                src={imageUrl(
                  data?.propertyDetails?.imageDetails[0]?.imageStoragePath
                )}
                id={`saved-prop-image-${data?.propertyDetails?.propertyId}`}
                onError={() => setHasError(true)}
                alt="property"
                className="preference-property-image"
                onClick={() => onNavigatePDP(data?.propertyDetails)}
              />
              <div className="fav-icons">
                <FaHeart id={`saved-prop-fav-${data?.propertyDetails?.propertyId}`} color="#ff5a5f" onClick={() => removeFromFavs(data)} />
              </div>
            </div>

            <div className="right-data-container">
              <div className="bid-data">
                <BidText data={data} />
                {data && <PriceAndLabel item={data} />}
                {data?.propertyListingDetails?.listingEndingInfo && (
                  <span className="countdown-timer">
                    <LuClock9 />
                    <Timer
                      isSidebarVisible={isSidebarVisible}
                      initialTime={
                        data?.propertyListingDetails?.listingEndingInfo
                      }
                    />
                  </span>
                )}
              </div>

              <p
                id={`saved-prop-address-${data?.propertyDetails?.propertyId}`}
                className="address"
                onClick={() => onNavigatePDP(data?.propertyDetails)}
              >
                {data?.propertyDetails?.address?.streetNumber}{" "}
                {data?.propertyDetails?.address?.streetName}
              </p>

              <div className="preference-button">
                <BidButton
                  propertyDetails={data?.propertyDetails}
                  propertyListingDetails={data?.propertyListingDetails}
                  cifCompleted={data?.cifCompleted}
                  fcProxyBidEnabled={data?.fcProxyBidEnabled}
                  isBestBidAvailable={data?.isBestBidAvailable}
                  isHnbEnded={data?.isHnbEnded}
                  sellerFinalNegotiationResponse={
                    data?.sellerFinalNegotiationResponse
                  }
                  userRegisteredForFcProxyBid={
                    data?.userRegisteredForFcProxyBid
                  }
                  userId={data?.userId}
                  isPhoneNumberVerified={user?.user?.phoneNumberVerified}
                />
                <button
                  id={`saved-prop-view-details-${data?.propertyDetails?.propertyId}`}
                  className="btn btn-sm hubzu-btn-thm"
                  onClick={() =>
                    onNavigatePDP(data?.propertyDetails, "button")
                  }
                >
                  View Details
                </button>
              </div>

              {(data?.propertyListingDetails?.ownItNowFlag && data?.propertyDetails?.commonAttributes?.status !== "REVIEW")&& (
                <>
                  <p className="text-center">OR</p>
                  <div className="preference-button">
                    <button
                      id={`saved-prop-end-auction-${data?.propertyDetails?.propertyId}`}
                      className="btn btn-sm btn-thm"
                      onClick={() => handleEndAuctionClick(data)}
                    >
                      End Auction Now
                    </button>
                    for ${data?.propertyListingDetails?.ownItNowPrice}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

      {hasMore && !loading && propertyData.length > 0 && (
        <div className="d-flex justify-content-center">
          <button id="saved-prop-load-more-btn" onClick={handleLoadMore} className="btn hubzu-btn-thm">
            Load More
          </button>
        </div>
      )}
    </>
  );
};

export default SavedProperties;
